import { sampleQueryKeys } from '@/config/queryKeys';
import { useDateFormatter, useScrollIntoViewOnData } from '@/hooks';
import { apiClient } from '@/lib/api-client';
import type { PaginatedSampleProteoformsResponse, SampleProteoformCountPublic, SampleProteoformPublic } from '@/types';
import { FullLoading } from '@components/Loaders';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-enterprise';
import React, { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

interface ProteoformDetailRendererProps {
  data: SampleProteoformCountPublic;
  orgId: string;
}

export const ProteoformDetailRenderer: FC<ProteoformDetailRendererProps> = ({ data, orgId }) => {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const detailPanelRef = useRef<HTMLDivElement>(null);

  const detailColumns: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      { headerName: t('proteoforms.name'), field: 'proteoform_name', minWidth: 180, flex: 1 },
      {
        headerName: t('proteoforms.abundance'),
        field: 'proteoform_abundance',
        minWidth: 60,
        valueFormatter: ({ value }) => value?.toLocaleString() ?? '',
      },
      { headerName: t('proteoforms.wellPosition'), field: 'well_position', minWidth: 60 },
      {
        headerName: t('proteoforms.createdAt'),
        field: 'created_at',
        minWidth: 180,
        valueFormatter: ({ value }) => formatDate(value),
      },
    ],
    [formatDate, t],
  );

  const {
    data: detailData,
    isError,
    isLoading,
  } = useQuery<PaginatedSampleProteoformsResponse | null>({
    queryKey: sampleQueryKeys.proteoformsSearch(orgId, data.sample_id),
    queryFn: async () => {
      const requestBody = {
        skip: 0,
        limit: 500,
        sort_by: 'proteoform_name',
        sort_order: 'desc',
        filters: {},
      };
      const response = await apiClient.post<PaginatedSampleProteoformsResponse>(
        sampleQueryKeys.endpoints.proteoformsSearch(orgId, data.sample_id),
        requestBody,
      );
      return response ?? null;
    },
    enabled: !!orgId && !!data.sample_id,
    retry: 1,
  });

  const hasData = !!detailData && Array.isArray(detailData.records) && detailData.records.length > 0;
  useScrollIntoViewOnData<HTMLDivElement>(detailPanelRef, isLoading, hasData, isError);

  return isLoading ? (
    <FullLoading fullHeight fullWidth label={t('proteoforms.fetchingSampleData')} ref={detailPanelRef} />
  ) : (
    <Table
      ariaLabel={t('proteoforms.tableAriaLabel')}
      columnDefs={detailColumns}
      tableId="proteoform-detail-table"
      container={{
        className: styles.masterDetailContainer,
        ref: detailPanelRef,
      }}
      domLayout="autoHeight"
      enableResponsiveResize
      masterDetail={false}
      rowModelType="clientSide"
      zebraStriping
      pagination
      paginationAutoPageSize
      rowData={hasData ? detailData.records : []}
    />
  );
};
